import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  Search,
  Filter,
  Download,
  Eye,
  Mail,
  Phone,
  Calendar,
  Briefcase,
  ExternalLink,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  UserCheck,
  Trash2,
  Star,
  Video,
  MessageSquare,
  BarChart3,
  ArrowRight
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ComparisonModal from '../components/ComparisonModal';
import ScoringPanel from '../components/ScoringPanel';
import InterviewScheduler from '../components/InterviewScheduler';
import EmailComposer from '../components/EmailComposer';
import { jobApplicationsAPI, jobsAPI } from '../../services/api';

const JobApplicationsPage = () => {
  const [applications, setApplications] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedJob, setSelectedJob] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // New features state
  const [selectedApplications, setSelectedApplications] = useState([]);
  const [showComparison, setShowComparison] = useState(false);
  const [showScoringPanel, setShowScoringPanel] = useState(false);
  const [showInterviewScheduler, setShowInterviewScheduler] = useState(false);
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [scoringApplicationId, setScoringApplicationId] = useState(null);
  const [minScore, setMinScore] = useState('');

  // Fetch applications and jobs
  React.useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      
      // Fetch applications
      const appsResponse = await jobApplicationsAPI.getAll();
      const appsData = appsResponse.data;
      
      // Fetch jobs for filter
      const jobsResponse = await jobsAPI.getAll();
      const jobsData = jobsResponse.data;
      
      // Ensure we have arrays
      setApplications(Array.isArray(appsData) ? appsData : []);
      setJobs(Array.isArray(jobsData?.jobs) ? jobsData.jobs : Array.isArray(jobsData) ? jobsData : []);
    } catch (error) {
      console.error('Error fetching data:', error);
      setApplications([]);
      setJobs([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'new':
        return <Clock className="w-4 h-4" />;
      case 'screening':
        return <FileText className="w-4 h-4" />;
      case 'shortlisted':
        return <CheckCircle className="w-4 h-4" />;
      case 'interview-scheduled':
        return <Calendar className="w-4 h-4" />;
      case 'interviewed':
        return <Calendar className="w-4 h-4" />;
      case 'offer':
        return <CheckCircle className="w-4 h-4" />;
      case 'rejected':
        return <XCircle className="w-4 h-4" />;
      case 'hired':
        return <CheckCircle className="w-4 h-4" />;
      case 'withdrawn':
        return <XCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      screening: 'bg-blue-100 text-blue-800 border-blue-200',
      shortlisted: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      'interview-scheduled': 'bg-purple-100 text-purple-800 border-purple-200',
      interviewed: 'bg-purple-100 text-purple-800 border-purple-200',
      offer: 'bg-green-100 text-green-800 border-green-200',
      rejected: 'bg-red-100 text-red-800 border-red-200',
      hired: 'bg-green-100 text-green-800 border-green-200',
      withdrawn: 'bg-gray-100 text-gray-800 border-gray-200'
    };

    const statusLabels = {
      'new': 'New',
      'screening': 'Screening',
      'shortlisted': 'Shortlisted',
      'interview-scheduled': 'Interview Scheduled',
      'interviewed': 'Interviewed',
      'offer': 'Offer',
      'rejected': 'Rejected',
      'hired': 'Hired',
      'withdrawn': 'Withdrawn'
    };

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${styles[status] || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
        {getStatusIcon(status)}
        <span className="ml-2">{statusLabels[status] || status}</span>
      </span>
    );
  };

  const handleStatusChange = async (applicationId, newStatus) => {
    try {
      await jobApplicationsAPI.updateStatus(applicationId, newStatus);
      
      setApplications(applications.map(app => 
        app.id === applicationId ? { ...app, status: newStatus } : app
      ));
      if (selectedApplication?.id === applicationId) {
        setSelectedApplication({ ...selectedApplication, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleDelete = async (applicationId) => {
    if (!window.confirm('Are you sure you want to delete this application?')) return;

    try {
      await jobApplicationsAPI.delete(applicationId);
      setApplications(applications.filter(app => app.id !== applicationId));
      setShowDetailModal(false);
    } catch (error) {
      console.error('Error deleting application:', error);
    }
  };

  const viewDetails = (application) => {
    setSelectedApplication(application);
    setShowDetailModal(true);
  };

  // New feature handlers
  const handleSelectApplication = (id) => {
    setSelectedApplications(prev => {
      if (prev.includes(id)) {
        return prev.filter(appId => appId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleCompareSelected = () => {
    if (selectedApplications.length >= 2) {
      setShowComparison(true);
    }
  };

  const handleOpenScoring = (applicationId) => {
    setScoringApplicationId(applicationId);
    setShowScoringPanel(true);
  };

  const handleOpenInterview = (applicationId) => {
    setScoringApplicationId(applicationId);
    setShowInterviewScheduler(true);
  };

  const handleOpenEmail = (applicationId) => {
    setScoringApplicationId(applicationId);
    setShowEmailComposer(true);
  };

  const getScoreColor = (score) => {
    if (!score) return 'text-gray-400';
    if (score >= 4.0) return 'text-green-600 font-semibold';
    if (score >= 3.0) return 'text-yellow-600 font-semibold';
    return 'text-red-600 font-semibold';
  };

  // Filter applications
  const filteredApplications = applications.filter(app => {
    const matchesSearch = 
      app.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesJob = !selectedJob || app.jobId === parseInt(selectedJob);
    const matchesStatus = !selectedStatus || app.status === selectedStatus;
    const score = app.overallScore || app.overall_score;
    const matchesScore = !minScore || (score && score >= parseFloat(minScore));

    return matchesSearch && matchesJob && matchesStatus && matchesScore;
  });

  // Statistics
  const stats = {
    total: applications.length,
    new: applications.filter(a => a.status === 'new').length,
    screening: applications.filter(a => a.status === 'screening').length,
    shortlisted: applications.filter(a => a.status === 'shortlisted').length,
    interviewed: applications.filter(a => a.status === 'interviewed' || a.status === 'interview-scheduled').length,
    hired: applications.filter(a => a.status === 'hired').length
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-96">
          <LoadingSpinner />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Job Applications</h1>
            <p className="text-gray-600 mt-1">Manage and review candidate applications</p>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total</p>
                <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <Users className="w-10 h-10 text-blue-500" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">New</p>
                <p className="text-3xl font-bold text-yellow-600">{stats.new}</p>
              </div>
              <Clock className="w-10 h-10 text-yellow-500" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Screening</p>
                <p className="text-3xl font-bold text-blue-600">{stats.screening}</p>
              </div>
              <Eye className="w-10 h-10 text-blue-500" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Interview Stage</p>
                <p className="text-3xl font-bold text-purple-600">{stats.interviewed}</p>
              </div>
              <UserCheck className="w-10 h-10 text-purple-500" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-xl shadow-sm p-6 border border-gray-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Hired</p>
                <p className="text-3xl font-bold text-green-600">{stats.hired}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
          </motion.div>
        </div>

        {/* Filters and Actions */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <select
              value={selectedJob}
              onChange={(e) => setSelectedJob(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Jobs</option>
              {jobs.map(job => (
                <option key={job.id} value={job.id}>{job.title}</option>
              ))}
            </select>

            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Status</option>
              <option value="new">New</option>
              <option value="screening">Screening</option>
              <option value="shortlisted">Shortlisted</option>
              <option value="interview-scheduled">Interview Scheduled</option>
              <option value="interviewed">Interviewed</option>
              <option value="offer">Offer</option>
              <option value="rejected">Rejected</option>
              <option value="hired">Hired</option>
              <option value="withdrawn">Withdrawn</option>
            </select>

            <input
              type="number"
              placeholder="Min Score (0-5)"
              value={minScore}
              onChange={(e) => setMinScore(e.target.value)}
              step="0.1"
              min="0"
              max="5"
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Bulk Actions */}
          {selectedApplications.length > 0 && (
            <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <span className="text-sm font-medium text-blue-900">
                {selectedApplications.length} selected
              </span>
              <button
                onClick={handleCompareSelected}
                disabled={selectedApplications.length < 2}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  selectedApplications.length >= 2
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                <BarChart3 className="w-4 h-4 inline mr-2" />
                Compare Candidates
              </button>
              <button
                onClick={() => setSelectedApplications([])}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                Clear Selection
              </button>
            </div>
          )}
        </div>

        {/* Applications Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-4 text-left">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300"
                      checked={selectedApplications.length === filteredApplications.length && filteredApplications.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedApplications(filteredApplications.map(app => app.id));
                        } else {
                          setSelectedApplications([]);
                        }
                      }}
                    />
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Applicant
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Job Position
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Score
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Applied Date
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredApplications.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                      No applications found
                    </td>
                  </tr>
                ) : (
                  filteredApplications.map((app) => (
                    <tr key={app.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-3 py-4">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300"
                          checked={selectedApplications.includes(app.id)}
                          onChange={() => handleSelectApplication(app.id)}
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">
                            {app.firstName?.[0]}{app.lastName?.[0]}
                          </div>
                          <div className="ml-3">
                            <p className="font-semibold text-gray-900">
                              {app.firstName} {app.lastName}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center text-sm text-gray-900">
                          <Briefcase className="w-4 h-4 mr-2 text-gray-400" />
                          {jobs.find(j => j.id === app.jobId)?.title || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1 text-sm">
                          <div className="flex items-center text-gray-600">
                            <Mail className="w-4 h-4 mr-2" />
                            {app.email}
                          </div>
                          {app.phone && (
                            <div className="flex items-center text-gray-600">
                              <Phone className="w-4 h-4 mr-2" />
                              {app.phone}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <Star className={`w-4 h-4 mr-1 ${getScoreColor(app.overallScore || app.overall_score)}`} fill="currentColor" />
                          <span className={`font-semibold ${getScoreColor(app.overallScore || app.overall_score)}`}>
                            {(() => {
                              const score = app.overallScore || app.overall_score;
                              if (!score) return 'N/A';
                              const numScore = typeof score === 'string' ? parseFloat(score) : score;
                              return !isNaN(numScore) ? numScore.toFixed(1) : 'N/A';
                            })()}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                          {app.createdAt || app.created_at
                            ? new Date(app.createdAt || app.created_at).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })
                            : 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <select
                          value={app.status}
                          onChange={(e) => handleStatusChange(app.id, e.target.value)}
                          className="text-sm border-0 bg-transparent font-medium focus:ring-2 focus:ring-blue-500 rounded-lg cursor-pointer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <option value="new">New</option>
                          <option value="screening">Screening</option>
                          <option value="shortlisted">Shortlisted</option>
                          <option value="interview-scheduled">Interview Scheduled</option>
                          <option value="interviewed">Interviewed</option>
                          <option value="offer">Offer</option>
                          <option value="rejected">Rejected</option>
                          <option value="hired">Hired</option>
                          <option value="withdrawn">Withdrawn</option>
                        </select>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleOpenScoring(app.id)}
                            className="inline-flex items-center px-3 py-1.5 bg-yellow-50 text-yellow-700 rounded-lg hover:bg-yellow-100 transition-colors text-sm font-medium"
                            title="Score Application"
                          >
                            <Star className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleOpenInterview(app.id)}
                            className="inline-flex items-center px-3 py-1.5 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors text-sm font-medium"
                            title="Schedule Interview"
                          >
                            <Video className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleOpenEmail(app.id)}
                            className="inline-flex items-center px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
                            title="Send Email"
                          >
                            <MessageSquare className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => viewDetails(app)}
                            className="inline-flex items-center px-3 py-1.5 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Detail Modal */}
        {showDetailModal && selectedApplication && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="sticky top-0 bg-white border-b border-gray-200 px-8 py-6 flex justify-between items-center z-10">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {selectedApplication.firstName} {selectedApplication.lastName}
                  </h2>
                  <p className="text-gray-600 mt-1">
                    Applied for: {jobs.find(j => j.id === selectedApplication.jobId)?.title}
                  </p>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                >
                  ×
                </button>
              </div>

              <div className="px-8 py-6 space-y-6">
                {/* Status */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                  <select
                    value={selectedApplication.status}
                    onChange={(e) => handleStatusChange(selectedApplication.id, e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="pending">Pending</option>
                    <option value="reviewing">Reviewing</option>
                    <option value="interview">Interview</option>
                    <option value="rejected">Rejected</option>
                    <option value="hired">Hired</option>
                  </select>
                </div>

                {/* Contact Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                    <a href={`mailto:${selectedApplication.email}`} className="text-blue-600 hover:underline flex items-center">
                      <Mail className="w-4 h-4 mr-2" />
                      {selectedApplication.email}
                    </a>
                  </div>
                  {selectedApplication.phone && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
                      <a href={`tel:${selectedApplication.phone}`} className="text-blue-600 hover:underline flex items-center">
                        <Phone className="w-4 h-4 mr-2" />
                        {selectedApplication.phone}
                      </a>
                    </div>
                  )}
                </div>

                {/* Social Links */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {selectedApplication.linkedin && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">LinkedIn</label>
                      <a href={selectedApplication.linkedin} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center">
                        <ExternalLink className="w-4 h-4 mr-2" />
                        View Profile
                      </a>
                    </div>
                  )}
                  {selectedApplication.github && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">GitHub</label>
                      <a href={selectedApplication.github} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center">
                        <ExternalLink className="w-4 h-4 mr-2" />
                        View Profile
                      </a>
                    </div>
                  )}
                  {selectedApplication.portfolio && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Portfolio</label>
                      <a href={selectedApplication.portfolio} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center">
                        <ExternalLink className="w-4 h-4 mr-2" />
                        View Portfolio
                      </a>
                    </div>
                  )}
                </div>

                {/* Experience */}
                {selectedApplication.experience && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Professional Experience</label>
                    <div className="bg-gray-50 rounded-lg p-4 text-gray-700 whitespace-pre-wrap">
                      {selectedApplication.experience}
                    </div>
                  </div>
                )}

                {/* Cover Letter */}
                {selectedApplication.coverLetter && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Cover Letter</label>
                    <div className="bg-gray-50 rounded-lg p-4 text-gray-700 whitespace-pre-wrap">
                      {selectedApplication.coverLetter}
                    </div>
                  </div>
                )}

                {/* Resume */}
                {selectedApplication.resumeUrl && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Resume</label>
                    <a
                      href={`${process.env.NODE_ENV === 'development' ? 'http://localhost:5001' : ''}${selectedApplication.resumeUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Resume
                    </a>
                  </div>
                )}

                {/* Notes */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Internal Notes</label>
                  <textarea
                    value={selectedApplication.notes || ''}
                    onChange={(e) => setSelectedApplication({ ...selectedApplication, notes: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Add notes about this applicant..."
                  />
                </div>

                {/* Actions */}
                <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                  <button
                    onClick={() => handleDelete(selectedApplication.id)}
                    className="inline-flex items-center px-4 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors font-medium"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Application
                  </button>
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Comparison Modal */}
        {showComparison && (
          <ComparisonModal
            applications={applications.filter(a => selectedApplications.includes(a.id))}
            onClose={() => setShowComparison(false)}
          />
        )}

        {/* Scoring Panel */}
        {showScoringPanel && (
          <ScoringPanel
            application={applications.find(app => app.id === scoringApplicationId)}
            onClose={() => setShowScoringPanel(false)}
            onUpdate={() => {
              setShowScoringPanel(false);
              fetchData();
            }}
            useOldAPI={true}
          />
        )}

        {/* Interview Scheduler */}
        {showInterviewScheduler && (
          <InterviewScheduler
            application={applications.find(app => app.id === scoringApplicationId)}
            onClose={() => setShowInterviewScheduler(false)}
            onSchedule={() => {
              setShowInterviewScheduler(false);
              fetchData();
            }}
          />
        )}

        {/* Email Composer */}
        {showEmailComposer && (
          <EmailComposer
            application={applications.find(app => app.id === scoringApplicationId)}
            onClose={() => setShowEmailComposer(false)}
            onSend={() => {
              setShowEmailComposer(false);
              fetchData();
            }}
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default JobApplicationsPage;
